import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { CAFE_INFO, WHATSAPP_ORDER_URL, WHATSAPP_RESERVE_URL, IMAGES } from "../data/cafe";

export default function Contact() {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    CAFE_INFO.mapsQuery,
  )}`;
  const mapsEmbed = `https://www.google.com/maps?q=${encodeURIComponent(
    CAFE_INFO.mapsQuery,
  )}&output=embed`;

  return (
    <div data-testid="page-contact" className="pt-32 pb-24 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
          Contact
        </span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl mt-4 leading-[1.05]">
          Come find us in <span className="italic text-gradient">Mysore.</span>
        </h1>
        <p className="text-white/60 mt-6 max-w-2xl text-lg">
          Drop by for a chilled Avil Milk or WhatsApp us to order ahead.
        </p>

        <div className="mt-16 grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: info cards */}
          <div className="lg:col-span-2 space-y-4">
            <Card
              icon={<MapPin size={18} />}
              title="Visit"
              body={CAFE_INFO.address}
              action={{ href: mapsUrl, label: "Open in Google Maps", testid: "contact-maps-link" }}
            />
            <Card
              icon={<Phone size={18} />}
              title="Call"
              body={CAFE_INFO.phone}
              action={{
                href: `tel:${CAFE_INFO.phone.replace(/\s+/g, "")}`,
                label: "Tap to call",
                testid: "contact-call-link",
              }}
            />
            <Card
              icon={<MessageCircle size={18} />}
              title="WhatsApp"
              body="Fastest way to place an order or book a table"
              action={{
                href: WHATSAPP_ORDER_URL,
                label: "Message on WhatsApp",
                testid: "contact-whatsapp-order",
                external: true,
              }}
            />
            <Card
              icon={<Mail size={18} />}
              title="Email"
              body={CAFE_INFO.email}
              action={{
                href: `mailto:${CAFE_INFO.email}`,
                label: "Send us a note",
                testid: "contact-email-link",
              }}
            />
            <Card
              icon={<Clock size={18} />}
              title="Hours"
              body={
                <ul className="text-white/80 space-y-1">
                  {CAFE_INFO.hours.map((h) => (
                    <li key={h.day} className="text-sm">
                      <span className="text-white/60">{h.day}: </span>
                      {h.time}
                    </li>
                  ))}
                </ul>
              }
            />
          </div>

          {/* Right: map + storefront */}
          <div className="lg:col-span-3 space-y-6">
            <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[16/10] bg-[#0a0a0a]">
              <iframe
                title="mismos on the map"
                src={mapsEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
                style={{ border: 0, filter: "grayscale(30%) contrast(1.05) brightness(0.9)" }}
              />
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[16/9]">
              <img
                src={IMAGES.storefront}
                alt="mismos storefront"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Bottom CTA band */}
        <div className="mt-20 rounded-3xl border border-white/10 p-8 md:p-12 bg-[#0a0a0a] text-center">
          <h2 className="font-display text-3xl md:text-5xl">
            Hungry now? We&apos;re a message away.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={WHATSAPP_ORDER_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cta-order"
              className="px-7 py-4 rounded-full bg-warm-gradient text-black font-medium hover:opacity-90 transition"
            >
              Order on WhatsApp
            </a>
            <a
              href={WHATSAPP_RESERVE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cta-reserve"
              className="px-7 py-4 rounded-full border border-white/20 hover:border-[#FF8C5A] hover:text-[#FF8C5A] transition"
            >
              Reserve a Table
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = ({ icon, title, body, action }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 rounded-full bg-[#FF8C5A]/10 text-[#FF8C5A] grid place-items-center border border-[#FF8C5A]/20 shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
          {title}
        </div>
        <div className="text-white/85 mt-1 break-words">{body}</div>
        {action && (
          <a
            href={action.href}
            target={action.external ? "_blank" : undefined}
            rel={action.external ? "noreferrer" : undefined}
            data-testid={action.testid}
            className="inline-block mt-3 text-sm text-gradient font-medium"
          >
            {action.label} →
          </a>
        )}
      </div>
    </div>
  </div>
);
